import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  CircularProgress,
  Card,
  CardContent,
  CardActions,
  TextField,
  Switch,
  FormControlLabel,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid
} from '@mui/material';
import { Add as AddIcon, Edit as EditIcon, Delete as DeleteIcon } from '@mui/icons-material';
import { WritingTenet } from '../../types';
import { apiService } from '../../services/api';

interface TenetDialogProps {
  open: boolean;
  tenet: WritingTenet | null;
  onClose: () => void;
  onSave: (tenet: Partial<WritingTenet>) => void;
}

const TenetDialog: React.FC<TenetDialogProps> = ({ open, tenet, onClose, onSave }) => {
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [active, setActive] = useState(true);
  const [errors, setErrors] = useState<{ name?: string; description?: string }>({});

  useEffect(() => {
    if (tenet) {
      setName(tenet.name);
      setDescription(tenet.description);
      setActive(tenet.active);
    } else {
      setName('');
      setDescription('');
      setActive(true);
    }
    setErrors({});
  }, [tenet, open]);

  const validateForm = () => {
    const newErrors: { name?: string; description?: string } = {};
    
    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }
    
    if (!description.trim()) {
      newErrors.description = 'Description is required';
    }
    
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSave = () => {
    if (!validateForm()) return;

    onSave({
      id: tenet?.id,
      name: name.trim(),
      description: description.trim(),
      active
    });
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {tenet ? 'Edit Writing Tenet' : 'Add Writing Tenet'}
      </DialogTitle>
      
      <DialogContent>
        <Box sx={{ pt: 1 }}>
          <TextField
            fullWidth
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            error={!!errors.name}
            helperText={errors.name}
            margin="normal"
          />
          
          <TextField
            fullWidth
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            error={!!errors.description}
            helperText={errors.description}
            margin="normal"
            multiline
            rows={4}
          />
          
          <FormControlLabel
            control={
              <Switch
                checked={active}
                onChange={(e) => setActive(e.target.checked)}
              />
            }
            label="Active"
            sx={{ mt: 2 }}
          />
        </Box>
      </DialogContent>
      
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button onClick={handleSave} variant="contained">
          {tenet ? 'Update' : 'Add'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

const WritingTenetsConfig: React.FC = () => {
  const [tenets, setTenets] = useState<WritingTenet[]>([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingTenet, setEditingTenet] = useState<WritingTenet | null>(null);

  const loadTenets = async () => {
    setLoading(true);
    setError(null);

    console.log('🔍 Loading writing tenets...');
    const response = await apiService.getWritingTenets();
    
    console.log('📡 Full API response:', response);
    console.log('📊 Response data:', response?.data);

    if (response?.error) {
      console.log('❌ Error details:', response.error);
      setError(response.error.error.message);
      setTenets([]);
    } else {
      // The backend returns: { success: true, data: WritingTenet[], timestamp, requestId }
      // So the actual tenets array is at response.data.data
      let tenetsData: WritingTenet[] = [];
      
      if (response?.data) {
        const responseData = response.data as any; // Type assertion to handle nested structure
        
        if (Array.isArray(responseData)) {
          // Direct array response
          console.log('✅ Data is direct array:', responseData);
          tenetsData = responseData;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Backend response format: { success: true, data: WritingTenet[] }
          console.log('✅ Data is in response.data.data:', responseData.data);
          tenetsData = responseData.data;
        } else {
          console.log('⚠️ Unexpected data structure:', responseData);
          tenetsData = [];
        }
      } else {
        console.log('⚠️ No data in response');
      }
      
      console.log('📋 Final tenets data:', tenetsData);
      console.log('📏 Tenets array length:', tenetsData.length);
      setTenets(Array.isArray(tenetsData) ? tenetsData : []);
    }

    setLoading(false);
  };

  useEffect(() => {
    loadTenets();
  }, []);

  const handleSaveTenets = async (updatedTenets: WritingTenet[]) => {
    setSaving(true);
    setError(null);
    setSuccess(null);

    const response = await apiService.updateWritingTenets(updatedTenets);

    if (response?.error) {
      setError(response.error.error.message);
    } else {
      // The backend returns: { success: true, data: WritingTenet[], timestamp, requestId }
      // So the actual tenets array is at response.data.data
      let tenetsData: WritingTenet[] = [];
      
      if (response?.data) {
        const responseData = response.data as any; // Type assertion to handle nested structure
        
        if (Array.isArray(responseData)) {
          // Direct array response
          tenetsData = responseData;
        } else if (responseData.data && Array.isArray(responseData.data)) {
          // Backend response format: { success: true, data: WritingTenet[] }
          tenetsData = responseData.data;
        } else {
          tenetsData = [];
        }
      }
      
      console.log('Update tenets API response:', response);
      console.log('Updated tenets data:', tenetsData);
      setTenets(Array.isArray(tenetsData) ? tenetsData : []);
      setSuccess('Writing tenets updated successfully!');
    }

    setSaving(false);
  };

  const handleAddTenet = () => {
    setEditingTenet(null);
    setDialogOpen(true);
  };

  const handleEditTenet = (tenet: WritingTenet) => {
    setEditingTenet(tenet);
    setDialogOpen(true);
  };

  const handleDeleteTenet = (tenetId: string) => {
    if (!Array.isArray(tenets)) return;
    const updatedTenets = tenets.filter(t => t.id !== tenetId);
    handleSaveTenets(updatedTenets);
  };

  const handleDialogSave = (tenetData: Partial<WritingTenet>) => {
    if (!Array.isArray(tenets)) return;
    
    let updatedTenets: WritingTenet[];

    if (editingTenet) {
      // Update existing tenet
      updatedTenets = tenets.map(t => 
        t.id === editingTenet.id 
          ? { ...t, ...tenetData, updatedAt: new Date() }
          : t
      );
    } else {
      // Add new tenet
      const newTenet: WritingTenet = {
        id: `tenet_${Date.now()}`,
        name: tenetData.name!,
        description: tenetData.description!,
        active: tenetData.active!,
        createdAt: new Date()
      };
      updatedTenets = [...tenets, newTenet];
    }

    handleSaveTenets(updatedTenets);
    setDialogOpen(false);
  };

  const handleToggleActive = (tenetId: string) => {
    if (!Array.isArray(tenets)) return;
    
    const updatedTenets = tenets.map(t => 
      t.id === tenetId 
        ? { ...t, active: !t.active }
        : t
    );
    handleSaveTenets(updatedTenets);
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={3}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Typography variant="h5">
          Writing Tenets Configuration
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={handleAddTenet}
          disabled={saving}
        >
          Add Tenet
        </Button>
      </Box>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 3 }}>
          {success}
        </Alert>
      )}

      <Grid container spacing={2}>
        {Array.isArray(tenets) && tenets.map((tenet) => (
          <Grid item xs={12} md={6} key={tenet.id}>
            <Card>
              <CardContent>
                <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={2}>
                  <Typography variant="h6" component="h3">
                    {tenet.name}
                  </Typography>
                  <FormControlLabel
                    control={
                      <Switch
                        checked={tenet.active}
                        onChange={() => handleToggleActive(tenet.id)}
                        disabled={saving}
                        size="small"
                      />
                    }
                    label="Active"
                    labelPlacement="start"
                  />
                </Box>
                
                <Typography variant="body2" color="text.secondary">
                  {tenet.description}
                </Typography>
              </CardContent>
              
              <CardActions>
                <IconButton
                  onClick={() => handleEditTenet(tenet)}
                  disabled={saving}
                  size="small"
                  aria-label="Edit"
                >
                  <EditIcon />
                </IconButton>
                <IconButton
                  onClick={() => handleDeleteTenet(tenet.id)}
                  disabled={saving}
                  size="small"
                  color="error"
                  aria-label="Delete"
                >
                  <DeleteIcon />
                </IconButton>
              </CardActions>
            </Card>
          </Grid>
        ))}
      </Grid>

      {tenets.length === 0 && !loading && (
        <Box textAlign="center" py={4}>
          <Typography variant="body1" color="text.secondary">
            No writing tenets configured. Add your first tenet to get started.
          </Typography>
        </Box>
      )}

      <TenetDialog
        open={dialogOpen}
        tenet={editingTenet}
        onClose={() => setDialogOpen(false)}
        onSave={handleDialogSave}
      />
    </Box>
  );
};

export default WritingTenetsConfig;